import { getApiUrl } from "../../config";
import { env } from "../../helpers/GlobalStateProvider";
import { getToken } from "../../helpers/tokenManager";

export const CHAT_HISTORY_KEY = "chatbotHistory";

// Helper function to get consistent user ID (same logic as ChatContainer)
const getCurrentUserId = async (): Promise<string> => {
  const token = getToken();
  if (!token) {
    if (process.env.NODE_ENV === 'development') console.log("[getCurrentUserId] No bearer token found");
    return "anonymousweb";
  }

  try {
    if (process.env.NODE_ENV === 'development') console.log("[getCurrentUserId] Fetching user info from API...");
    const userInfoResponse = await fetch(`${getApiUrl(env)}/v3/apiKey/info`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({})
    });
    
    if (userInfoResponse.ok) {
      const userInfo = await userInfoResponse.json();
      const userId = userInfo.email || userInfo.walletAddress || "anonymousweb";
      if (process.env.NODE_ENV === 'development') console.log(`[getCurrentUserId] Got user info - email: "${userInfo.email}", walletAddress: "${userInfo.walletAddress}", final userId: "${userId}"`);
      return userId;
    } else {
      if (process.env.NODE_ENV === 'development') console.log(`[getCurrentUserId] API request failed with status: ${userInfoResponse.status}`);
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') console.log("[getCurrentUserId] Error fetching user info:", e);
  }
  
  return "anonymousweb";
};

interface AgentConversation {
  id: string;
  title: string;
  lastActivity: number;
  createdAt: number;
  messageCount: number;
}

// Sync function for local storage
export const getLocalChatHistory = (): any[] => {
  const savedHistory = localStorage.getItem(CHAT_HISTORY_KEY);
  return savedHistory ? JSON.parse(savedHistory) : [];
};

// Async function to retrieve chat history from agent backend
export const getChatHistory = async (): Promise<any[]> => {
  const token = getToken();
  
  if (!token) {
    if (process.env.NODE_ENV === 'development') console.log('No bearer token found, using local chat history');
    return getLocalChatHistory();
  }

  // Get user info fresh from the API to ensure consistency with ChatContainer
  const userId = await getCurrentUserId();
  if (process.env.NODE_ENV === 'development') console.log(`[getChatHistory] Using user ID for conversations: "${userId}"`);
  
  if (token && userId !== "anonymousweb") {
    try {
      if (process.env.NODE_ENV === 'development') console.log('Fetching conversations from agent backend...');
      const response = await fetch(`${getApiUrl(env)}/agent/users/${userId}/conversations`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.status}`);
      }
      
      const { conversations } = await response.json();
      if (process.env.NODE_ENV === 'development') console.log(`Got ${conversations.length} conversations from agent backend`);
      
      // Convert agent conversations to our internal format
      const chatHistory = conversations.map((conv: AgentConversation) => ({
        id: Date.now() + Math.random(), // Generate a local ID for UI
        agentConversationId: conv.id, // Store the agent backend ID
        title: conv.title,
        messages: [], // We'll load messages when a conversation is selected
        lastActivity: conv.lastActivity,
        createdAt: conv.createdAt,
        messageCount: conv.messageCount,
      }));
      
      return chatHistory;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.log("Error fetching conversations from agent backend:", error);
    }
  }
  
  if (process.env.NODE_ENV === 'development') console.log('Using local chat history');
  return getLocalChatHistory();
};

// Save function to store chat history (with agent backend, this is mostly for local storage fallback)
export const saveChatHistory = async (history: any[]): Promise<void> => {
  // With the agent backend, conversations are managed automatically
  // We mainly use this for local storage fallback for anonymous users
  localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(history));
};

// Function to migrate local chat history to agent backend
export const migrateLocalChatHistory = async (): Promise<void> => {
  const token = getToken();
  
  if (!token) {
    if (process.env.NODE_ENV === 'development') console.log("No bearer token found; migration skipped.");
    return;
  }

  // Get user info using the same pattern as ChatContainer and getChatHistory
  const userId = await getCurrentUserId();
  
  if (userId !== "anonymousweb") {
    try {
      const historyToMigrate = getLocalChatHistory();
      
      if (historyToMigrate.length === 0) {
        if (process.env.NODE_ENV === 'development') console.log("No local history to migrate");
        return;
      }
      
      if (process.env.NODE_ENV === 'development') console.log(`Migrating ${historyToMigrate.length} local conversations to agent backend`);
      
      // Create conversations in the agent backend for each local chat
      for (const chat of historyToMigrate) {
        try {
          const response = await fetch(`${getApiUrl(env)}/agent/users/conversations`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              userId,
              title: chat.title || "Migrated Conversation",
            }),
          });
          
          if (response.ok) {
            if (process.env.NODE_ENV === 'development') console.log(`Migrated conversation: ${chat.title}`);
          }
        } catch (convError) {
          if (process.env.NODE_ENV === 'development') console.log(`Failed to migrate conversation: ${chat.title}`, convError);
        }
      }
      
      // Clear local storage after successful migration
      clearChatHistory();
      if (process.env.NODE_ENV === 'development') console.log("Migration completed, cleared local storage");
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.log("Error migrating chat history to agent backend:", error);
    }
  } else {
    if (process.env.NODE_ENV === 'development') console.log("User is anonymous; migration skipped.");
  }
};

// Clear function to remove chat history
export const clearChatHistory = (): void => {
  localStorage.removeItem(CHAT_HISTORY_KEY);
};

// Delete a conversation from the agent backend
export const deleteConversation = async (agentConversationId: string): Promise<boolean> => {
  const token = getToken();
  
  if (!token) return false;

  // Get user info using the same pattern as ChatContainer and getChatHistory
  const userId = await getCurrentUserId();
  
  if (token && userId !== "anonymousweb") {
    try {
      const response = await fetch(`${getApiUrl(env)}/agent/users/${userId}/conversations/${agentConversationId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        if (process.env.NODE_ENV === 'development') console.log(`Deleted conversation: ${agentConversationId}`);
        return true;
      } else {
        if (process.env.NODE_ENV === 'development') console.error(`Failed to delete conversation: ${response.status}`);
        return false;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error deleting conversation:", error);
      return false;
    }
  }
  
  return false;
};

// Rename a conversation in the agent backend
export const renameConversation = async (agentConversationId: string, newTitle: string): Promise<boolean> => {
  const token = getToken();
  
  if (!token) return false;

  // Get user info using the same pattern as ChatContainer and getChatHistory
  const userId = await getCurrentUserId();
  
  if (token && userId !== "anonymousweb") {
    try {
      const response = await fetch(`${getApiUrl(env)}/agent/users/${userId}/conversations/${agentConversationId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ title: newTitle }),
      });
      
      if (response.ok) {
        if (process.env.NODE_ENV === 'development') console.log(`Renamed conversation: ${agentConversationId} to "${newTitle}"`);
        return true;
      } else {
        if (process.env.NODE_ENV === 'development') console.error(`Failed to rename conversation: ${response.status}`);
        return false;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error renaming conversation:", error);
      return false;
    }
  }
  
  return false;
};

// Get conversation details including messages from agent backend
export const getConversationDetails = async (agentConversationId: string): Promise<any> => {
  const token = getToken();
  
  if (!token) return null;

  // Get user info using the same pattern as ChatContainer and getChatHistory
  const userId = await getCurrentUserId();
  
  if (token && userId !== "anonymousweb") {
    try {
      const response = await fetch(`${getApiUrl(env)}/agent/users/${userId}/conversations/${agentConversationId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const conversationDetails = await response.json();
        if (process.env.NODE_ENV === 'development') console.log(`Got conversation details: ${agentConversationId}`);
        return conversationDetails;
      } else {
        if (process.env.NODE_ENV === 'development') console.error(`Failed to get conversation details: ${response.status}`);
        return null;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error getting conversation details:", error);
      return null;
    }
  }
  
  return null;
};